import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { saveProfile } from '../utils/profileManager'
import { availableIcons } from '../utils/profileManager'
import LoadingSpinner from '../components/UI/LoadingSpinner'

const Onboarding = () => {
    const navigate = useNavigate()
    const { user, getCurrentUser } = useAuth()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const [formData, setFormData] = useState({
        raveName: user?.profileSettings?.raveName || '',
        favoriteFestival: user?.profileSettings?.favoriteFestival || '',
        favoriteGenre: user?.profileSettings?.favoriteGenre || '',
        tagline: user?.profileSettings?.tagline || '',
        selectedIcon: user?.profileSettings?.selectedIcon || 'user'
    })

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        setError('')
    }

    const handleIconSelect = (iconId) => {
        setFormData(prev => ({ ...prev, selectedIcon: iconId }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!formData.raveName.trim()) {
            setError('Rave alias is required to continue')
            return
        }

        if (formData.raveName.trim().length < 2) {
            setError('Rave alias must be at least 2 characters')
            return
        }

        setIsSubmitting(true)
        setError('')

        try {
            const profileData = {
                raveName: formData.raveName.trim(),
                favoriteFestival: formData.favoriteFestival.trim(),
                favoriteGenre: formData.favoriteGenre.trim(),
                tagline: formData.tagline.trim(),
                selectedIcon: formData.selectedIcon,
                profileType: 'icon'
            }

            const success = await saveProfile(profileData, true, getCurrentUser)

            if (success) {
                // Refresh user data to get updated profile
                if (getCurrentUser) {
                    await getCurrentUser()
                }
                navigate('/home')
            } else {
                setError('Failed to save profile. Please try again.')
            }
        } catch (err) {
            console.error('Onboarding error:', err)
            setError('An error occurred. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--theme-bg-primary)' }}>
            <div className="w-full max-w-lg">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center text-4xl"
                        style={{ background: 'linear-gradient(135deg, var(--theme-primary), var(--theme-secondary))' }}>
                        🎪
                    </div>
                    <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--theme-text-primary)' }}>
                        Welcome to Main Stage!
                    </h1>
                    <p style={{ color: 'var(--theme-text-secondary)' }}>
                        Let's set up your festival identity. Your rave alias is how friends will see you.
                    </p>
                </div>

                {/* Form Card */}
                <div className="rounded-2xl p-6 shadow-xl border"
                    style={{
                        background: 'var(--theme-bg-secondary)',
                        borderColor: 'var(--theme-border)'
                    }}>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Rave Alias - Required */}
                        <div>
                            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--theme-text-primary)' }}>
                                Rave Alias <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                name="raveName"
                                value={formData.raveName}
                                onChange={handleChange}
                                placeholder="e.g., NeonDreamer, BassQueen, etc."
                                maxLength={30}
                                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                                style={{
                                    background: 'var(--theme-bg-primary)',
                                    borderColor: 'var(--theme-border)',
                                    color: 'var(--theme-text-primary)'
                                }}
                                autoFocus
                            />
                            <p className="text-xs mt-1" style={{ color: 'var(--theme-text-muted)' }}>
                                This is the name friends will see on your passport. No real names for privacy!
                            </p>
                        </div>

                        {/* Icon Selection */}
                        <div>
                            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--theme-text-primary)' }}>
                                Choose Your Icon
                            </label>
                            <div className="grid grid-cols-6 gap-2">
                                {availableIcons.slice(0, 12).map((icon) => (
                                    <button
                                        key={icon.id}
                                        type="button"
                                        onClick={() => handleIconSelect(icon.id)}
                                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all ${
                                            formData.selectedIcon === icon.id
                                                ? 'ring-2 ring-offset-2 scale-110'
                                                : 'hover:scale-105'
                                        }`}
                                        style={{
                                            background: formData.selectedIcon === icon.id
                                                ? 'linear-gradient(135deg, var(--theme-primary), var(--theme-secondary))'
                                                : 'var(--theme-bg-primary)',
                                            borderColor: 'var(--theme-border)',
                                            ringColor: 'var(--theme-primary)'
                                        }}
                                        title={icon.name}
                                    >
                                        {icon.icon}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Optional Fields */}
                        <div className="pt-4 border-t" style={{ borderColor: 'var(--theme-border)' }}>
                            <p className="text-sm mb-4" style={{ color: 'var(--theme-text-muted)' }}>
                                Optional - You can always update these later in Settings
                            </p>

                            {/* Favorite Festival */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text-secondary)' }}>
                                    Favorite Festival
                                </label>
                                <input
                                    type="text"
                                    name="favoriteFestival"
                                    value={formData.favoriteFestival}
                                    onChange={handleChange}
                                    placeholder="e.g., EDC, Tomorrowland, Coachella"
                                    maxLength={30}
                                    className="w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                                    style={{
                                        background: 'var(--theme-bg-primary)',
                                        borderColor: 'var(--theme-border)',
                                        color: 'var(--theme-text-primary)'
                                    }}
                                />
                            </div>

                            {/* Favorite Genre */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text-secondary)' }}>
                                    Favorite Genre
                                </label>
                                <input
                                    type="text"
                                    name="favoriteGenre"
                                    value={formData.favoriteGenre}
                                    onChange={handleChange}
                                    placeholder="e.g., House, Techno, Bass, Trance"
                                    maxLength={30}
                                    className="w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                                    style={{
                                        background: 'var(--theme-bg-primary)',
                                        borderColor: 'var(--theme-border)',
                                        color: 'var(--theme-text-primary)'
                                    }}
                                />
                            </div>

                            {/* Tagline */}
                            <div>
                                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text-secondary)' }}>
                                    Tagline
                                </label>
                                <input
                                    type="text"
                                    name="tagline"
                                    value={formData.tagline}
                                    onChange={handleChange}
                                    placeholder="Your festival motto or vibe"
                                    maxLength={60}
                                    className="w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all"
                                    style={{
                                        background: 'var(--theme-bg-primary)',
                                        borderColor: 'var(--theme-border)',
                                        color: 'var(--theme-text-primary)'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="p-3 rounded-xl text-sm text-red-200 bg-red-500/20 border border-red-500/30">
                                {error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting || !formData.raveName.trim()}
                            className="w-full py-4 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            style={{
                                background: 'linear-gradient(135deg, var(--theme-primary), var(--theme-secondary))'
                            }}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <LoadingSpinner size="sm" />
                                    Saving...
                                </span>
                            ) : (
                                "Let's Go! 🎉"
                            )}
                        </button>
                    </form>
                </div>

                {/* Privacy Note */}
                <p className="text-center text-xs mt-4" style={{ color: 'var(--theme-text-muted)' }}>
                    Your real name will never be shown to other users. Only your rave alias is visible on your passport.
                </p>
            </div>
        </div>
    )
}

export default Onboarding
